import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { BrowserExtensionControls } from '@/components/astro/new-design-25/SEO/browser-extension/browser-extension-controls';
import { BrowserExtensionFeatures } from '@/components/astro/new-design-25/SEO/browser-extension/browser-extension-features';
import { BrowserExtensionHero } from '@/components/astro/new-design-25/SEO/browser-extension/browser-extension-hero';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly';
import { SeoContent } from '@/components/astro/new-design-25/SEO/seo-content';
import { FAQ } from '@/components/astro/new-design-25/ui/FAQ';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { StartListeningCTA } from '@/components/astro/new-design-25/ui/start-listening-cta';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { GridBackground } from '@/components/shared-ui/grid-background';
import { seo } from '@/seo/seo';

const browserExtensionFAQs = [
  {
    question: 'Which browsers are supported?',
    answer:
      'Currently, we support Chrome, Edge, Brave, Opera, Arc, and Vivaldi.',
  },
  {
    question: 'Does the extension work on all websites?',
    answer:
      'The extension works on most websites including news sites, blogs, research papers, and documentation. Some sites with special security restrictions may limit extension functionality for security reasons.',
  },
  {
    question: 'How do I disable the extension on specific sites?',
    answer:
      "Click the extension icon in your browser toolbar, then toggle off 'Enable on this site' in the popup. You can also manage site permissions through your browser's extension settings page.",
  },
  {
    question: 'Can I upload documents through the extension?',
    answer:
      "Yes! You can upload PDFs, Word documents, and other text files directly through the extension. There's also an email upload feature where you can send documents to a special email address for processing.",
  },
  {
    question: 'Can I sync my content across devices?',
    answer:
      'Yes! When you create an account and send content to the web app, it syncs across all your devices. You can start listening on your computer and continue on your phone seamlessly.',
  },
];

const seoSections = [
  {
    title: 'Transform Any Website Into Your Personal Audio Library',
    content:
      "Browser extensions have revolutionized how we consume web content, and our text-to-speech browser extension takes this convenience to the next level. Whether you're browsing news articles, research papers, blog posts, or online documentation, simply highlight any text and instantly convert it to natural-sounding audio. Perfect for busy professionals who want to listen during commutes, students who learn better through audio, or anyone seeking a hands-free browsing experience.",
    contentSecondary:
      'Our browser extension goes beyond basic text-to-speech functionality by offering intelligent document processing, seamless cross-device synchronization, and advanced playback controls. Upload PDFs directly from your browser, send documents via email for instant processing, or capture entire webpages for complete audio conversion. With support for Chrome and Firefox, you can transform any website into an engaging audiobook experience while maintaining full control over speed, voice selection, and content organization.',
  },
  {
    title:
      'Why Choose Our Extension Over Read Aloud and Speechify Alternatives',
    content:
      'While popular extensions like Read Aloud offer basic webpage reading and Speechify provides general text-to-speech functionality, academic and professional users need specialized features that generic tools cannot deliver. Our browser extension combines the accessibility of Read Aloud with advanced document processing capabilities specifically designed for complex content including academic papers, technical documentation, and research materials.',
    contentSecondary:
      'The key advantage lies in our intelligent content handling: while Read Aloud reads everything on a page including navigation menus and advertisements, our extension focuses on main content with smart filtering that removes distractions. Unlike Speechify\'s limited browser functionality, we offer seamless integration between web browsing and document management, allowing you to build a comprehensive audio library directly from your browser. Users consistently report superior performance on academic and technical content, with testimonials noting "finally found an extension that handles scientific documents properly" and "much better than Read Aloud for research papers."',
  },
];

export const Route = createFileRoute('/_marketing/_seo/browser-extension')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Text to Speech Browser Extension | Chrome & Firefox Webpage Reader | Listening',
        description:
          'Install our browser extension to convert any webpage to audio. Highlight text, upload documents, and listen with natural AI voices. Chrome & Firefox.',
      }),
    ],
  }),
  component: () => (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>
      <LinksSrOnly />
      <div className='relative'>
        <GridBackground>
          <div className='relative z-10'>
            <NavBar />
            <main id='main-content' tabIndex={-1}>
              <BrowserExtensionHero />
              <BrowserExtensionControls />
              <BrowserExtensionFeatures />
              <StartListeningCTA />
            </main>
            <SeoContent sections={seoSections} />
            <Testimonials />
            <div
              className='bg-linear-to-b from-azure-525/5 to-azure-525/20'
              role='complementary'
            >
              <FAQ
                description='Find answers to common questions about using our Listening browser extension.'
                faqs={browserExtensionFAQs}
                showApplyButton={false}
                title='Frequently Asked Questions'
              />
              <TryListeningBanner />
              <Footer />
            </div>
          </div>
        </GridBackground>
      </div>
    </>
  ),
});
